import React, { lazy, Suspense } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Route, Switch } from "react-router-dom";
import { ConnectedRouter } from "connected-react-router";
import LoadingBar from "react-redux-loading-bar";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import {
  Authenticated,
  NoMatch,
  Initializer,
  history,
  PortfolioInitializer,
  ScrollToTop,
} from "utils";

import { isAuthenticated } from "utils/Authenticated";
import { ProtectedRoute } from "utils/routing";

import Navigation from "containers/Navigation";
import Calendar from "containers/Calendar";
import Notifications from "containers/Notifications";
import Dashboard from "containers/Dashboard";
import UserConfirmModal from "containers/UserConfirmModal";
import ResetPassword from "containers/ResetPassword";
import Subscriptions from "containers/Settings/components/Profile/Subscriptions";
import AssetCreateModal from "components/AssetCreateModal";
import BrokerInviteModal from "components/BrokerInviteModal";
import DealModal from "components/DealModal";
import Loading from "components/Loading";
import PortfolioModal from "containers/PortfolioModal";
import PortfolioDeleteModal from "containers/PortfolioDeleteModal";
import LinkedinOAuthCallback from "utils/auth/LinkedinOAuthCallback";
import ErrorBoundary from "utils/ErrorBoundary";
import Modals from "modals";

import styles from "./App.css";

const AssetDetail = lazy(() => import("containers/AssetDetail"));
const AssetFlyer = lazy(() => import("containers/AssetFlyer"));
const Assets = lazy(() => import("containers/Assets"));
const AssetLeads = lazy(() => import("containers/AssetLeads"));
const AssetLeadDetail = lazy(() => import("containers/AssetLeadDetail"));
const CompanyPage = lazy(() => import("containers/CompanyPage"));
const DealDetail = lazy(() => import("containers/DealDetail"));
const Deals = lazy(() => import("containers/Deals"));
const Login = lazy(() => import("containers/Login"));
const Proposal = lazy(() => import("containers/Proposal"));
const Settings = lazy(() => import("containers/Settings"));
const SignUp = lazy(() => import("containers/SignUp"));

const App = ({
  isAssetModalOpen,
  isBrokerInviteModalOpen,
  isDealModalOpen,
  isUserConfirmModalOpen,
}) => (
  <ConnectedRouter history={history}>
    <ScrollToTop>
      <div className={styles.app}>
        <ErrorBoundary>
          <DndProvider backend={HTML5Backend}>
            <Notifications />
            <Modals />
            <LoadingBar className={styles.loader} />
            <Suspense fallback={<Loading />}>
              <Route path="/oauth/linkedin" component={LinkedinOAuthCallback} />
              <Route path="/signup" component={SignUp} condition={() => !isAuthenticated()} />
              <ProtectedRoute
                exact
                path="/login"
                component={Login}
                condition={() => !isAuthenticated()}
              />
              <Route path="/reset_password" component={ResetPassword} />
              <Route path="/settings/subscriptions" component={Subscriptions} />
              <Route exact path="/proposals/:proposalToken" component={Proposal} />
              <Route exact path="/flyers/:assetID" component={AssetFlyer} />
              <Route exact path="/companies/:companyID" component={CompanyPage} />
            </Suspense>
            <Authenticated>
              <Initializer>
                <Navigation />
                {isAssetModalOpen && <AssetCreateModal />}
                {isBrokerInviteModalOpen && <BrokerInviteModal />}
                {isDealModalOpen && <DealModal />}
                {isUserConfirmModalOpen && <UserConfirmModal />}
                <PortfolioModal />
                <PortfolioDeleteModal />
                <div className={styles.container}>
                  <Suspense fallback={<Loading />}>
                    <Switch>
                      <ProtectedRoute
                        path="/assets/:assetID"
                        component={AssetDetail}
                        requiredPermissions="assets"
                      />
                      <ProtectedRoute
                        exact
                        path="/deals/:dealID"
                        component={DealDetail}
                        requiredPermissions="deals"
                      />
                      <ProtectedRoute
                        exact
                        path="/asset_leads/:assetLeadID"
                        component={AssetLeadDetail}
                        requiredPermissions="assetLeads"
                      />
                      <Route exact path="/calendar" component={Calendar} />
                      <Route exact path="/dashboard" component={Dashboard} />
                      <Route path="/settings" component={Settings} />
                      <PortfolioInitializer>
                        <Switch>
                          <ProtectedRoute
                            exact
                            path="/deals"
                            component={Deals}
                            requiredPermissions="deals"
                          />
                          <ProtectedRoute
                            exact
                            path="/assets"
                            component={Assets}
                            requiredPermissions="assets"
                          />
                          <ProtectedRoute
                            exact
                            path="/asset_leads"
                            component={AssetLeads}
                            requiredPermissions="assetLeads"
                          />
                          <Route component={NoMatch} />
                        </Switch>
                      </PortfolioInitializer>
                    </Switch>
                  </Suspense>
                </div>
              </Initializer>
            </Authenticated>
          </DndProvider>
        </ErrorBoundary>
      </div>
    </ScrollToTop>
  </ConnectedRouter>
);

App.propTypes = {
  isAssetModalOpen: PropTypes.bool,
  isBrokerInviteModalOpen: PropTypes.bool,
  isDealModalOpen: PropTypes.bool,
  isUserConfirmModalOpen: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  isAssetModalOpen: state.isAssetModalOpen,
  isBrokerInviteModalOpen: state.isBrokerInviteModalOpen,
  isDealModalOpen: state.isDealModalOpen,
  isUserConfirmModalOpen: !!state.currentUser.id && !state.currentUser.active,
});

export default connect(mapStateToProps)(App);
